import domainModel from 'domain-model';
import { useState } from 'react';
import type { OrderFlow, OrderFlowRule } from 'domain-model/types/oms';
import { Alert, Button, Card, CardBody, CardHeader, Badge } from 'reactstrap';

const { orderFlowChecks }: { orderFlowChecks: OrderFlow[] } = domainModel.order;

interface RuleViewerProps {
  value: { id: string }[];
  showStateHeaders?: boolean;
  showRuleNumbers?: boolean;
  compact?: boolean;
}

const stateLabels = {
  received: 'Mottagen',
  waitingForRestock: 'Väntar på påfyllning',
  approvedByDropshipSupplier: 'Godkänd av dropship-leverantör',
  enRoute: 'På väg',
  delivered: 'Levererad',
};

const stateDescriptions = {
  received: 'Regler som körs när en order först tas emot i systemet',
  waitingForRestock:
    'Regler som körs för order som väntar på påfyllning av lager',
  approvedByDropshipSupplier:
    'Regler som körs när en dropship-leverantör har godkänt ordern',
  enRoute: 'Regler som körs när ordern är på väg till kunden',
  delivered: 'Regler som körs när ordern har levererats',
};

export default function RuleViewer({
  value,
  showStateHeaders = true,
  showRuleNumbers = true,
  compact = false,
}: RuleViewerProps) {
  const [expandedRules, setExpandedRules] = useState<Set<string>>(new Set());

  // Get all rules that match the selected IDs, organized by state
  const getSelectedRulesByState = () => {
    const rulesByState: {
      [key: string]: { state: string; rules: OrderFlowRule[] };
    } = {};

    orderFlowChecks.forEach(orderFlow => {
      const selectedRules = orderFlow.rules.filter(rule =>
        value.some(v => v.id === rule.id),
      );
      if (selectedRules.length > 0) {
        rulesByState[orderFlow.state] = {
          state: orderFlow.state,
          rules: selectedRules,
        };
      }
    });

    return rulesByState;
  };

  const toggleRuleExpansion = (ruleId: string) => {
    const newExpanded = new Set(expandedRules);
    if (newExpanded.has(ruleId)) {
      newExpanded.delete(ruleId);
    } else {
      newExpanded.add(ruleId);
    }
    setExpandedRules(newExpanded);
  };

  if (!value) {
    return (
      <div className="alert alert-info d-flex align-items-center">
        <div>
          <strong>Inga regler valda - Standardreglerna används</strong>
          <p className="mb-0">
            Välj regler med knappen: &apos;Sätt Orderflödesregler&apos; för att
            se dem här.
          </p>
        </div>
      </div>
    );
  }

  const totalSelectedRules = value.length;
  if (totalSelectedRules === 0) {
    return (
      <div className="alert alert-warning d-flex align-items-center">
        <div>
          <strong>
            Inga regler ska gälla för detta lager! - Orderbotten kommer inte att
            arbeta för detta lagerställe
          </strong>
          <p className="mb-0">
            Välj regler med knappen: &apos;Sätt Orderflödesregler&apos; för att
            se dem här.
          </p>
        </div>
      </div>
    );
  }

  let ruleCounter = 0;
  const selectedRulesByState = getSelectedRulesByState();
  return (
    <div className="rule-viewer">
      {/* Summary */}
      <Card className="bg-light mt-4">
        <CardBody>
          <div className="row text-center">
            <div className="col-md-4">
              <div className="h4 text-primary mb-1">{totalSelectedRules}</div>
              <div className="small text-muted">Totalt antal regler</div>
            </div>

            <div className="col-md-4">
              <div className="h4 mb-1">
                {Object.values(selectedRulesByState).reduce(
                  (sum, { rules }) =>
                    sum +
                    rules.reduce(
                      (checkSum, rule) => checkSum + rule.checks.length,
                      0,
                    ),
                  0,
                )}
              </div>
              <div className="small text-muted">Totalt antal kontroller</div>
            </div>
            <div className="col-md-4">
              <div className="h4 mb-1 text-success">
                {Object.values(selectedRulesByState).reduce(
                  (sum, { rules }) =>
                    sum +
                    rules.filter(rule => rule.onAllChecksPassAction).length,
                  0,
                )}
              </div>
              <div className="small text-muted">Regler med åtgärder</div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Rules by State */}
      {Object.entries(selectedRulesByState).map(([state, { rules }]) => (
        <Card key={state} className="mb-4">
          {showStateHeaders && (
            <CardHeader className="bg-light">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h3 className="h5 mb-1">
                    När:{' '}
                    {stateLabels[state as keyof typeof stateLabels] || state}
                  </h3>
                  <p className="text-muted small mb-0">
                    {stateDescriptions[state as keyof typeof stateDescriptions]}
                  </p>
                </div>
                <Badge color="primary">
                  {rules.length} regel{rules.length !== 1 ? 'er' : ''}
                </Badge>
              </div>
            </CardHeader>
          )}

          {/* Rules */}
          <CardBody className="ms-2">
            {rules.map(rule => {
              ruleCounter++;
              const isExpanded = expandedRules.has(rule.id);

              return (
                <Card
                  key={rule.id}
                  className={`mb-3 ${compact ? 'border-0 shadow-sm' : ''}`}
                >
                  <CardHeader className="bg-white">
                    <div className="d-flex align-items-start">
                      {showRuleNumbers && (
                        <Badge color="secondary" className="me-3 rounded-pill">
                          {ruleCounter}
                        </Badge>
                      )}
                      <div className="flex-grow-1">
                        <h4 className="h6 mb-2">{rule.name}</h4>
                        <div className="d-flex flex-wrap gap-2 mb-2">
                          <Badge color="info">
                            {rule.checks.length} kontroll
                            {rule.checks.length !== 1 ? 'er' : ''}
                          </Badge>
                          {rule.onAllChecksPassActionLocalizedName && (
                            <Badge color="success">
                              <i className="bi bi-play-fill me-1"></i>
                              Åtgärd: {rule.onAllChecksPassActionLocalizedName}
                            </Badge>
                          )}
                          {rule.dontEvaluateMoreRulesWhenFails && (
                            <Badge color="warning">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              Stoppar vid fel
                            </Badge>
                          )}
                        </div>
                        <Button
                          color="link"
                          size="sm"
                          className="p-0 text-decoration-none"
                          onClick={() => toggleRuleExpansion(rule.id)}
                        >
                          <i
                            className={`bi bi-chevron-${
                              isExpanded ? 'up' : 'down'
                            } me-1`}
                          ></i>
                          {isExpanded ? 'Dölj detaljer' : 'Visa detaljer'}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>

                  {isExpanded && (
                    <CardBody>
                      {/* Checks */}
                      <div className="mb-4">
                        <h5 className="h6 mb-1">
                          <i className="bi bi-check-circle text-success me-2"></i>
                          Kontroller som utförs:
                        </h5>
                        <div className="row g-2">
                          {rule.checks.map((check, index) => (
                            <div key={index} className="col-12">
                              <div className="p-3 bg-light rounded">
                                <div className="d-flex align-items-start">
                                  <Badge
                                    color="success"
                                    className="me-2 mt-1 rounded-pill"
                                  >
                                    {index + 1}
                                  </Badge>
                                  <div>
                                    <p className="mb-1 fw-medium">
                                      {check.description}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Action */}
                      {rule.onAllChecksPassActionLocalizedName && (
                        <div className="mb-3">
                          <h5 className="h6 mb-1">
                            <i className="bi bi-gear text-primary me-2"></i>
                            Åtgärd vid godkända kontroller:
                          </h5>
                          <Alert color="primary" className="mb-0">
                            <div className="d-flex align-items-center">
                              <i className="bi bi-play-circle-fill me-2"></i>
                              <div>
                                <strong>
                                  {rule.onAllChecksPassActionLocalizedName}
                                </strong>
                              </div>
                            </div>
                          </Alert>
                        </div>
                      )}

                      {/* Rule ID for debugging */}
                      <div className="text-muted small">
                        <strong>Regel-ID:</strong> <code>{rule.id}</code>
                      </div>
                    </CardBody>
                  )}
                </Card>
              );
            })}
          </CardBody>
        </Card>
      ))}
    </div>
  );
}
